import { useNavigate } from '@tanstack/react-router';
import { LogOut, User as UserIcon, ChevronDown, Building2 } from 'lucide-react';
import { ThemeToggle } from '@/components/layout/theme';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useMe, useLogout } from '@/hooks/use-auth';
import { useAuthStore } from '@/lib/auth-store';
import { roleLabel } from '@/lib/utils';

export function Topbar() {
  const navigate = useNavigate();
  const { data: meData } = useMe();
  const orgSlug = useAuthStore((s) => s.orgSlug);
  const logout = useLogout();

  const user = meData?.user;
  const orgName = meData?.user.org.name ?? orgSlug ?? '—';

  const initials = user
    ? `${user.firstName?.[0] ?? user.email[0] ?? '?'}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : '?';

  return (
    <header className="flex h-14 items-center justify-between border-b bg-card/30 px-4 backdrop-blur-sm">
      {/* Org context — read-only badge for now; will become a switcher in lot 2I */}
      <div className="flex items-center gap-2 font-mono text-xs">
        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-muted-foreground">org</span>
        <span className="text-foreground">{orgName}</span>
        {orgSlug && orgSlug !== orgName && (
          <span className="id-pill">{orgSlug}</span>
        )}
      </div>

      <div className="flex items-center gap-1">
        <ThemeToggle />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2 px-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-xs font-medium">
                {initials}
              </div>
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {user && (
              <>
                <DropdownMenuLabel>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-normal text-foreground">
                      {user.firstName} {user.lastName}
                    </span>
                    <span className="text-2xs font-normal text-muted-foreground">{user.email}</span>
                    <span className="font-mono text-2xs uppercase tracking-wider text-primary">
                      {roleLabel(user.role)}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onSelect={() => navigate({ to: '/profile' })}>
              <UserIcon />
              <span>Profile</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              destructive
              onSelect={() => logout.mutate(undefined, { onSuccess: () => navigate({ to: '/login' }) })}
            >
              <LogOut />
              <span>Sign out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
